import { useTranslation } from "react-i18next";
import { ContractCoverage } from "../../redux/contracts/contracts-types";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeadCell,
    TableRow,
} from "../../components/Table";
import { Paper } from "@mui/material";
import ActionButton from "./ActionButton";
import { AppDispatch } from "../../interfaces/RootState";
import { displayDateTime } from "../../utils/utils";
import React from "react";
import { ContractStateCell } from "./ContractStateCell";

/**
 * One row in a {@link ContractsTable}.
 */
export type Row = {
    /**
     * Table-unique key for this row (for React).
     */
    key: number;

    /**
     * Type of the contract (translated), such as "Company DPA".
     */
    type: string;

    /**
     * Contract data. May be `null` if there is no contract yet.
     */
    contractCoverage: ContractCoverage;

    /**
     * Reason why the sign action is disabled. Passed to {@link ActionButton}.
     */
    signActionDisabledReason: string | null;

    /**
     * Callback to be called when the sign action is clicked. Passed to {@link ActionButton}.
     */
    onSignClicked: () => void;

    /**
     * Test ID for the action button.
     */
    actionButtonTestId?: string;

    dispatch: AppDispatch;
};

/**
 * Props for {@link ContractsTable}.
 */
export type Props = {
    /**
     * Redux dispatch function.
     */
    dispatch: AppDispatch;

    /**
     * Rows to be displayed in the table.
     */
    rows: Row[];
};

/**
 * Table that displays contracts and their {@link ActionButton}s.
 */
const ContractsRow: React.FC<Row> = ({
    key,
    type,
    contractCoverage,
    signActionDisabledReason,
    onSignClicked,
    actionButtonTestId,
    dispatch,
}) => {
    const signButton = (
        <ActionButton
            dispatch={dispatch}
            contract={contractCoverage.contract}
            signActionDisabledReason={signActionDisabledReason}
            onSignClicked={onSignClicked}
            testId={actionButtonTestId}
        />
    );

    const coveredElsewhere = contractCoverage.coveredByFramework || contractCoverage.coveredByUserContract;
    const shouldShowSignButton = !coveredElsewhere;

    const updatedAt = contractCoverage.coveredByFramework?.updatedAt || contractCoverage.contract?.createdAt || null;
    const formattedUpdateString = updatedAt ? displayDateTime(new Date(updatedAt)) : "-";

    return (
        <TableRow key={key}>
            <TableCell>{type}</TableCell>
            <ContractStateCell contractCoverage={contractCoverage} disableTooltip={false} />
            <TableCell>{formattedUpdateString}</TableCell>
            <TableCell>{shouldShowSignButton && signButton}</TableCell>
        </TableRow>
    );
};

/**
 * Table that displays contracts and their action buttons for a principal manager.
 */
export const ContractsTable: React.FC<Props> = ({ dispatch, rows }) => {
    const { t } = useTranslation();

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeadCell>{t("contracts.table.header.contractType")}</TableHeadCell>
                        <TableHeadCell>{t("contracts.table.header.state")}</TableHeadCell>
                        <TableHeadCell>{t("contracts.table.header.updatedAt")}</TableHeadCell>
                        <TableHeadCell></TableHeadCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <ContractsRow
                            key={row.key}
                            type={row.type}
                            contractCoverage={row.contractCoverage}
                            signActionDisabledReason={row.signActionDisabledReason}
                            onSignClicked={row.onSignClicked}
                            actionButtonTestId={row.actionButtonTestId}
                            dispatch={dispatch}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
